// Industry widget-gallery data. Ported verbatim from the design-team build
// (16 verticals); the renderer that consumes these types lives in
// components/marketing/widget-gallery/. Each vertical describes the placements
// (Homepage, Collection, PDP, Lookbook, Community, Reviews) and the widget
// mockups inside each — illustrative layouts merchants can browse for
// inspiration before configuring their own.
import accessoriesJewellery from './data/accessories-jewellery.json'
import automobiles from './data/automobiles.json'
import b2bSaas from './data/b2b-saas.json'
import babyKids from './data/baby-kids.json'
import beautySkincare from './data/beauty-skincare.json'
import consumerElectronics from './data/consumer-electronics.json'
import edtech from './data/edtech.json'
import fashionApparel from './data/fashion-apparel.json'
import foodBeverage from './data/food-beverage.json'
import furniture from './data/furniture.json'
import healthWellness from './data/health-wellness.json'
import homeLifestyle from './data/home-lifestyle.json'
import hospitality from './data/hospitality.json'
import petCare from './data/pet-care.json'
import sportingGoods from './data/sporting-goods.json'
import travelExperiences from './data/travel-experiences.json'

// A tile inside a widget (post, product card, photo).
export interface GalleryTile {
  kw: string
  src?: string
  dur?: string
  pill?: string
  cap?: string
  caption?: string
  label?: string
  play?: boolean
  buy?: string
  name?: string
  price?: string
  add?: string
  text?: string
  stars?: number
}

// One widget mockup. Fields are a superset across all 19 renderer types; each
// renderer reads only the ones it needs.
export interface GalleryWidget {
  type: string
  name: string
  tag?: string
  num: string
  note?: string
  full?: boolean
  // common content
  title?: string
  eyebrow?: string
  // collections of tiles (most widgets) — either rich tiles or bare keywords
  tiles?: Array<GalleryTile | string>
  items?: Array<GalleryTile & { label?: string }>
  // proof wall / review rail
  hashtag?: string
  rating?: string
  count?: string
  score?: string
  sources?: Array<{ name: string; score: string }>
  reviews?: Array<{ stars?: number; text: string; name: string; src: string }>
  cards?: Array<{ kw: string; text: string; name: string; src: string }>
  // inline strip
  url?: string
  seeAll?: string
  // tabbed
  tabs?: string[]
  sorts?: string[]
  // player / conversion
  kw?: string
  source?: string
  tags?: string[]
  kicker?: string
  priceUnit?: string
  price?: string
  cta?: string
  cta2?: string
  clip?: string
  ratingSrc?: string
  strip?: string[]
  stripTitle?: string
  sub?: string
  fields?: Array<{ label: string; val: string }>
  // editorial
  ctaLead?: string
  // map
  pins?: string[]
  // shop set / spotlight
  handle?: string
  avatar?: string
  stat?: string
  quote?: string
  // upload
  consent?: string
  // trust badge
  source_?: string
}

export interface GallerySection {
  id: string
  short: string
  title: string
  lead: string
  widgets: GalleryWidget[]
}

export interface IndustryGallery {
  name: string
  slug: string
  noun: string
  eyebrow: string
  headline: string
  intro: string
  tags: string[]
  sections: GallerySection[]
}

// gallery-slug → data
const GALLERIES: Record<string, IndustryGallery> = {
  'accessories-jewellery': accessoriesJewellery as unknown as IndustryGallery,
  automobiles: automobiles as unknown as IndustryGallery,
  'b2b-saas': b2bSaas as unknown as IndustryGallery,
  'baby-kids': babyKids as unknown as IndustryGallery,
  'beauty-skincare': beautySkincare as unknown as IndustryGallery,
  'consumer-electronics': consumerElectronics as unknown as IndustryGallery,
  edtech: edtech as unknown as IndustryGallery,
  'fashion-apparel': fashionApparel as unknown as IndustryGallery,
  'food-beverage': foodBeverage as unknown as IndustryGallery,
  furniture: furniture as unknown as IndustryGallery,
  'health-wellness': healthWellness as unknown as IndustryGallery,
  'home-lifestyle': homeLifestyle as unknown as IndustryGallery,
  hospitality: hospitality as unknown as IndustryGallery,
  'pet-care': petCare as unknown as IndustryGallery,
  'sporting-goods': sportingGoods as unknown as IndustryGallery,
  'travel-experiences': travelExperiences as unknown as IndustryGallery,
}

// idukki industry detail slug (lib/copy/industries-detail.ts) → gallery slug.
// The two systems were authored separately and spell a few slugs differently.
export const INDUSTRY_TO_GALLERY: Record<string, string> = {
  'fashion-and-apparels': 'fashion-apparel',
  'beauty-and-cosmetics': 'beauty-skincare',
  'accessories-and-jewelery': 'accessories-jewellery',
  'home-lifestyle': 'home-lifestyle',
  furniture: 'furniture',
  hospitality: 'hospitality',
  'consumer-electronics': 'consumer-electronics',
  'sporting-goods': 'sporting-goods',
  'b2b-saas': 'b2b-saas',
  'baby-and-kids': 'baby-kids',
  'health-and-wellness': 'health-wellness',
  'food-and-beverage': 'food-beverage',
  automobiles: 'automobiles',
  edtech: 'edtech',
  'travel-and-experiences': 'travel-experiences',
  'pet-care': 'pet-care',
}

// Resolve a gallery from an idukki industry detail slug. Returns undefined if
// the vertical has no authored gallery yet.
export function galleryForIndustrySlug(industrySlug: string): IndustryGallery | undefined {
  const gallerySlug = INDUSTRY_TO_GALLERY[industrySlug]
  return gallerySlug ? GALLERIES[gallerySlug] : undefined
}

export function hasGalleryForIndustry(industrySlug: string): boolean {
  return Boolean(INDUSTRY_TO_GALLERY[industrySlug])
}

export { GALLERIES }
